import React, { useState, useEffect } from 'react';
// Blueprint removed — using Skyline dialog + native elements
import EnhancedDialog from './EnhancedDialog';
import type { EnrichedTableField, OrderBy, LimitOffset } from '../types/metadata';

import { useRecordAudit, invalidateAndRefetch, buildQueryForRecordAudit } from '../utils/metadata';
import type { AuditRecord } from '../utils/metadata';
import { useGlobalMetadata } from '../utils/providers';
import { liveChangeIncludesId } from '../utils/realtime/liveChanges';
import type { LiveChangeEventDetail } from '../utils/realtime/liveChanges';
import './RecordHistory.css';

// Grouped change events for display
interface GroupedChangeEvent {
  events: AuditRecord[];
  created_by_rel_name?: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  startTime: Date;
  endTime: Date;
}

// Individual field change for detailed view
interface FieldChange {
  field: string;
  oldValue: string;
  newValue: string;
}

// Component props interface
interface RecordHistoryProps {
  tableName: string;
  recordId: number | string;
}

function RecordHistory({ tableName, recordId }: RecordHistoryProps): React.JSX.Element {
  const [selectedEvent, setSelectedEvent] = useState<GroupedChangeEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  
  const metadata = useGlobalMetadata();
  const schemaName = metadata.data?.tableData?.byName?.[tableName]?.schema_name ?? '';
  
  // Safely access field metadata with null checks
  const fieldByName: Record<string, EnrichedTableField> = metadata?.data?.fieldData?.[tableName]?.byFieldName || {};

  const conditions = {
    parent_id: {
      filterType: 'number' as const,
      type: 'equals' as const,
      filter: recordId,
    },
  };

  const orderBy: OrderBy = {
    column: 'transaction_time',
    direction: 'descending',
  };

  const limitOffset: LimitOffset = {
    limit: 100,
    offset: 0,
  };

  const getRecordArgs = {
    tableName,
    schemaName,
    conditions,
    orderBy,
    limitOffset,
    withCount: false,
  };

  const {
    data: changeEvents = [],
    error,
    isLoading,
  } = useRecordAudit({
    enabled: !metadata.isLoading && !!schemaName,
    ...getRecordArgs,
  });

  useEffect(() => {
    // Subscribe to change events for real-time updates
    const handleTableChange = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<LiveChangeEventDetail>).detail;

      // For audit logs, a new audit entry is inserted into _{tableName}_audit.
      // However, our generic subscription is on the PARENT table.
      // If the parent record changes (INSERT/UPDATE/DELETE), a trigger writes to audit table.
      // So if we get an event for THIS recordId, we should refetch audit logs.

      if (schemaName && detail?.schema === schemaName && detail.table === tableName && liveChangeIncludesId(detail, recordId)) {
        invalidateAndRefetch(buildQueryForRecordAudit(getRecordArgs));
      }
    };

    // Listen to all operation types since any change to the record creates an audit entry
    const insertEventName = `${tableName}_insert`;
    const updateEventName = `${tableName}_update`;
    const deleteEventName = `${tableName}_delete`;

    globalThis.addEventListener(insertEventName, handleTableChange);
    globalThis.addEventListener(updateEventName, handleTableChange);
    globalThis.addEventListener(deleteEventName, handleTableChange);

    return () => {
      globalThis.removeEventListener(insertEventName, handleTableChange);
      globalThis.removeEventListener(updateEventName, handleTableChange);
      globalThis.removeEventListener(deleteEventName, handleTableChange);
    };
  }, [tableName, recordId, getRecordArgs]);

  // Group change events by created_by_rel.name, operation, and within a 10-minute window
  const groupChangeEvents = (events: AuditRecord[]): GroupedChangeEvent[] => {
    if (!events || events.length === 0) return [];

    // Clone and reverse the events array to process in ascending time order
    const eventsCopy = [...events].reverse();
    const groupedEvents: GroupedChangeEvent[] = [];
    let currentGroup: GroupedChangeEvent | null = null;

    eventsCopy.forEach((event) => {
      const eventTime = new Date(event.transaction_time);
      /* 
       * WORKAROUND: Cross-Schema Embedding Limitation in PostgREST
       * 
       * Background:
       * The audit tables (e.g. `public._funder_audit`) are in the 'public' schema, while the users
       * table (`core_crm.app_user`) is in the 'core_crm' schema. Although a valid Foreign Key exists
       * in the database connecting them, PostgREST treats different schemas (exposed via `db-schemas`)
       * as isolated API profiles when accessed via `Accept-Profile`.
       * 
       * Limitation:
       * When querying the 'public' profile (to access `_funder_audit`), resources from 'core_crm'
       * (like `app_user`) are NOT visible in the API definition for that profile. Consequently,
       * embedding requests like `select=...,created_by_rel:app_user(...)` fail with "Could not find
       * a relationship", even though the DB relationship is valid. PostgREST does not support
       * cross-profile embedding in a single request.
       * 
       * Current Solution (Frontend):
       * We manually resolve the user name using the `created_by` ID and the client-side metadata cache
       * (`clientSideSelectTables`). This works because `app_user` is small (<200 rows) and cached globally.
       * 
       * Potential Backend Solutions (for future reference):
       * 1. Public View: Create a VIEW in `public` (e.g., `public.app_user_view`) that selects from
       *    `core_crm.app_user`. This brings the resource into the 'public' API profile.
       * 2. Computed Relationship: Define a PostgreSQL function in `public` that takes the audit row
       *    and returns the `core_crm.app_user` record. PostgREST can treat this function as a
       *    "computed relationship" for embedding.
       *    Example: `CREATE FUNCTION public.app_user(public._funder_audit) RETURNS core_crm.app_user ...`
       */
      const createdByName = metadata.data?.clientSideSelectTables?.flattened?.[`app_user_${event.created_by}`]?.name || 'Unknown User';
      
      if (!currentGroup) {
        currentGroup = {
          events: [event],
          created_by_rel_name: createdByName,
          operation: event.operation,
          startTime: eventTime,
          endTime: eventTime,
        };
      } else {
        const timeDiff = (eventTime.getTime() - currentGroup.endTime.getTime()) / (1000 * 60); // in minutes
        
        if (
          createdByName === currentGroup.created_by_rel_name &&
          event.operation === currentGroup.operation &&
          timeDiff <= 10
        ) {
          currentGroup.events.push(event);
          currentGroup.endTime = eventTime;
        } else {
          groupedEvents.push(currentGroup);
          currentGroup = {
            events: [event],
            created_by_rel_name: createdByName,
            operation: event.operation,
            startTime: eventTime,
            endTime: eventTime,
          };
        }
      }
    });

    if (currentGroup) {
      groupedEvents.push(currentGroup);
    }

    // Reverse groupedEvents to restore descending order
    return groupedEvents.reverse();
  };

  const groupedChangeEvents = groupChangeEvents(changeEvents as AuditRecord[]);

  const handleViewDetails = (group: GroupedChangeEvent): void => {
    setSelectedEvent(group);
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setSelectedEvent(null);
    setIsModalOpen(false);
  };

  const renderOperations = (group: GroupedChangeEvent): string => {
    switch (group.operation) {
      case 'INSERT':
        return 'Create';
      case 'UPDATE':
        return 'Update';
      case 'DELETE':
        return 'Delete';
      default:
        return group.operation;
    }
  };

  const renderSummary = (group: GroupedChangeEvent): string => {
    const changedFieldsSet = new Set<string>();
    
    group.events.forEach((event) => {
      if (event.operation === 'INSERT') {
        const changedFields = Object.keys(event.new_data || {}).filter(
          (field) => event.new_data?.[field] !== null && event.new_data?.[field] !== undefined
        );
        changedFields.forEach((field) => changedFieldsSet.add(field));
      } else if (event.operation === 'UPDATE') {
        const changedFields = Object.keys(event.new_data || {});
        changedFields.forEach((field) => changedFieldsSet.add(field));
      }
    });
    
    const changedFields = Array.from(changedFieldsSet);
    const fieldsToShow = changedFields.slice(0, 4);
    let summary = fieldsToShow.map((field) => fieldByName[field]?.ui_label || field).join(', ');
    
    if (changedFields.length > 4) {
      const moreCount = changedFields.length - 4;
      summary += ` (+${moreCount} more)`;
    }
    
    return summary;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };
  
  // Updated renderChangeDetails to order events by transaction_time descending
  const renderChangeDetails = (group: GroupedChangeEvent): React.JSX.Element => {
    const sortedEvents = [...group.events].sort(
      (a, b) => new Date(b.transaction_time).getTime() - new Date(a.transaction_time).getTime()
    );
    
    return (
      <div className="change-details-card">
        {sortedEvents.map((event, idx) => (
          <div key={idx}>
            <p>
              <strong>At:</strong> {formatDate(event.transaction_time)}
            </p>
            
            {renderEventChanges(event)}
            <hr />
          </div>
        ))}
      </div>
    );
  };

  const renderEventChanges = (event: AuditRecord): React.JSX.Element => {
    const createdByName = metadata.data?.clientSideSelectTables?.flattened?.[`app_user_${event.created_by}`]?.name || 'Unknown User';
    const changes: FieldChange[] = [];
    
    if (event.operation === 'INSERT') {
      Object.keys(event.new_data || {}).forEach((field) => {
        const newValue = event.new_data?.[field];
        if (newValue !== null && newValue !== undefined) {
          changes.push({
            field,
            oldValue: 'blank',
            newValue: String(newValue),
          });
        }
      });
    } else if (event.operation === 'UPDATE') {
      Object.keys(event.new_data || {}).forEach((field) => {
        const oldValue = event.old_data?.[field];
        const newValue = event.new_data?.[field];
        
        changes.push({
          field,
          oldValue: oldValue !== null && oldValue !== undefined ? String(oldValue) : 'blank',
          newValue: newValue !== null && newValue !== undefined ? String(newValue) : 'blank',
        });
      });
    }
    return (
      <div>
        {changes.map((change, index) => (
          <p key={index}>
            <strong>{createdByName}</strong> changed{' '}
            <strong>{fieldByName[change.field]?.ui_label || change.field}</strong> from{' '}
            <strong>{change.oldValue}</strong> to <strong>{change.newValue}</strong>
          </p>
        ))}
      </div>
    );
  };

  if (isLoading || metadata.isLoading) {
    return <div>Loading history...</div>;
  }

  if (error || metadata.error) {
    const effectiveError = error || metadata.error;
    return <div>Error loading history: {effectiveError?.message || 'Unknown error'}</div>;
  }

  return (
    <div className="record-history">
      {groupedChangeEvents.length === 0 ? (
        <div className="no-changes">No changes to show</div>
      ) : (
        <>
          <div className="history-header">
            <div className="header-item">Date</div>
            <div className="header-item">Operation</div>
            <div className="header-item">User</div>
            <div className="header-item">Total Changes</div>
            <div className="header-item">Summary</div>
          </div>
          {groupedChangeEvents.map((group, index) => (
            <div
              key={index}
              className="history-row"
              onClick={() => handleViewDetails(group)}
            >
              <div className="row-item">
                {formatDate(group.events[0].transaction_time)}
              </div>
              <div className="row-item">{renderOperations(group)}</div>
              <div className="row-item">{group.created_by_rel_name}</div>
              <div className="row-item">{group.events.length}</div>
              <div className="row-item">{renderSummary(group)}</div>
            </div>
          ))}
        </>
      )}

      {selectedEvent && (
        <EnhancedDialog
          isOpen={isModalOpen}
          onClose={closeModal}
          title="Change Details"
          style={{ width: '600px' }}
        >
          <div className="sky-dialog-body">{renderChangeDetails(selectedEvent)}</div>
          <div className="sky-dialog-footer">
            <button className="sky-btn sky-btn-ghost" onClick={closeModal}>Close</button>
          </div>
        </EnhancedDialog>
      )}
    </div>
  );
}

export default RecordHistory;
